import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Chip,
    Tabs,
    Tab,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    Alert,
    Snackbar,
    Card,
    CardContent,
    Grid,
    Divider
} from '@mui/material';
import api from '../services/api';

const UserManagement = () => {
    const [users, setUsers] = useState([]);
    const [role, setRole] = useState('student');
    const [loading, setLoading] = useState(false);

    // Create User Dialog
    const [createOpen, setCreateOpen] = useState(false);
    const [departments, setDepartments] = useState([]);
    const [programs, setPrograms] = useState([]);
    const [createForm, setCreateForm] = useState({
        name: '', email: '', phone: '', password: '', role: 'student',
        roll_number: '', department_id: '', program_id: '',
        structure_type: 'Semester', structure_value: 1
    });

    // Bulk Semester Update
    const [bulkForm, setBulkForm] = useState({
        program_id: '', current_value: '', new_value: '', structure_type: ''
    });
    const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

    useEffect(() => {
        fetchUsers();
    }, [role]);

    useEffect(() => {
        fetchAcademicData();
    }, []);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const res = await api.get(`/admin/users?role=${role}`);
            setUsers(res.data.data);
        } catch (error) {
            console.error('Failed to fetch users:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchAcademicData = async () => {
        try {
            const [deptRes, progRes] = await Promise.all([
                api.get('/admin/departments'),
                api.get('/admin/programs')
            ]);
            setDepartments(deptRes.data.data);
            setPrograms(progRes.data.data);
        } catch (e) {
            console.error('Failed to fetch academic data:', e);
        }
    };

    const handleStatusUpdate = async (userId, isApproved, isActive) => {
        try {
            await api.put(`/admin/users/${userId}/status`, { is_approved: isApproved, is_active: isActive });
            fetchUsers();
        } catch (error) {
            alert('Update failed');
        }
    };

    const handleCreateUser = async () => {
        try {
            await api.post('/admin/users', createForm);
            setCreateOpen(false);
            setCreateForm({
                name: '', email: '', phone: '', password: '', role: 'student',
                roll_number: '', department_id: '', program_id: '',
                structure_type: 'Semester', structure_value: 1
            });
            fetchUsers();
            setSnack({ open: true, msg: 'User created successfully!', severity: 'success' });
        } catch (error) {
            setSnack({ open: true, msg: 'Failed to create user', severity: 'error' });
        }
    };

    const handleBulkUpdate = async () => {
        try {
            const res = await api.put('/admin/users/bulk-semester', bulkForm);
            setSnack({ open: true, msg: res.data.message, severity: 'success' });
            fetchUsers();
        } catch (error) {
            setSnack({ open: true, msg: 'Bulk update failed', severity: 'error' });
        }
    };

    const filteredPrograms = createForm.department_id
        ? programs.filter(p => p.department_id?.toString() === createForm.department_id?.toString())
        : programs;

    const maxValue = createForm.structure_type === 'Year' ? 4 : 8;
    const bulkMaxValue = bulkForm.structure_type === 'Year' ? 4 : 8;

    return (
        <Box p={4}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
                <Typography variant="h4">User Management</Typography>
                <Button variant="contained" onClick={() => setCreateOpen(true)}>Create User</Button>
            </Box>

            <Tabs value={role} onChange={(e, v) => setRole(v)} sx={{ mb: 2 }}>
                <Tab label="Students" value="student" />
                <Tab label="Faculty" value="faculty" />
                <Tab label="Parents" value="parent" />
            </Tabs>

            {/* Bulk Update Semester Section - Only show for Students tab */}
            {role === 'student' && (
                <Card sx={{ mb: 3, border: '1px solid #e0e0e0' }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ color: '#1976d2' }}>
                            Bulk Update Year / Semester
                        </Typography>
                        <Typography variant="body2" color="text.secondary" mb={2}>
                            Update all students in a program from one semester/year to another
                        </Typography>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={3}>
                                <TextField
                                    select label="Program" fullWidth size="small"
                                    value={bulkForm.program_id}
                                    onChange={(e) => setBulkForm({ ...bulkForm, program_id: e.target.value })}
                                >
                                    {programs.map(p => (
                                        <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={2}>
                                <TextField
                                    select label="Type" fullWidth size="small"
                                    value={bulkForm.structure_type}
                                    onChange={(e) => setBulkForm({ ...bulkForm, structure_type: e.target.value, current_value: '', new_value: '' })}
                                >
                                    <MenuItem value="Semester">Semester</MenuItem>
                                    <MenuItem value="Year">Year</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={2}>
                                <TextField
                                    select label="Current" fullWidth size="small"
                                    value={bulkForm.current_value}
                                    onChange={(e) => setBulkForm({ ...bulkForm, current_value: e.target.value })}
                                >
                                    <MenuItem value="">All</MenuItem>
                                    {[...Array(bulkMaxValue)].map((_, i) => (
                                        <MenuItem key={i + 1} value={i + 1}>{i + 1}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={2}>
                                <TextField
                                    select label="New" fullWidth size="small"
                                    value={bulkForm.new_value}
                                    onChange={(e) => setBulkForm({ ...bulkForm, new_value: e.target.value })}
                                >
                                    {[...Array(bulkMaxValue)].map((_, i) => (
                                        <MenuItem key={i + 1} value={i + 1}>{i + 1}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <Button
                                    variant="contained" color="warning" fullWidth
                                    disabled={!bulkForm.program_id || !bulkForm.new_value}
                                    onClick={handleBulkUpdate}
                                >
                                    Update All
                                </Button>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            )}

            <TableContainer component={Paper}>
                {loading ? (
                    <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
                ) : (
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Email</TableCell>
                                {role === 'student' && <TableCell>Roll No.</TableCell>}
                                {role === 'student' && <TableCell>Year/Sem</TableCell>}
                                <TableCell>Status</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {users.map((user) => (
                                <TableRow key={user.id}>
                                    <TableCell>{user.name}</TableCell>
                                    <TableCell>{user.email}</TableCell>
                                    {role === 'student' && <TableCell>{user.roll_number || '-'}</TableCell>}
                                    {role === 'student' && (
                                        <TableCell>
                                            <Chip
                                                label={`${user.structure_type || 'Sem'} ${user.structure_value || '-'}`}
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                            />
                                        </TableCell>
                                    )}
                                    <TableCell>
                                        <Chip
                                            label={user.is_approved ? 'Approved' : 'Pending'}
                                            color={user.is_approved ? 'success' : 'warning'}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {!user.is_approved && (
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                size="small"
                                                onClick={() => handleStatusUpdate(user.id, 1, 1)}
                                                sx={{ mr: 1 }}
                                            >
                                                Approve
                                            </Button>
                                        )}
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            size="small"
                                            onClick={() => handleStatusUpdate(user.id, user.is_approved, 0)}
                                        >
                                            Deactivate
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </TableContainer>

            {/* Create User Dialog */}
            <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Create New User</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <TextField label="Name" fullWidth value={createForm.name}
                        onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} />
                    <TextField label="Email" fullWidth value={createForm.email}
                        onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} />
                    <TextField label="Phone" fullWidth value={createForm.phone}
                        onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })} />
                    <TextField label="Password" type="password" fullWidth value={createForm.password}
                        onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })} />
                    <TextField select label="Role" fullWidth value={createForm.role}
                        onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })}>
                        <MenuItem value="student">Student</MenuItem>
                        <MenuItem value="faculty">Faculty</MenuItem>
                        <MenuItem value="parent">Parent</MenuItem>
                        <MenuItem value="admin">Admin</MenuItem>
                    </TextField>

                    {createForm.role === 'student' && (
                        <>
                            <Divider sx={{ my: 1 }} />
                            <Typography variant="subtitle2" color="primary">Student Details</Typography>
                            <TextField label="Roll Number" fullWidth value={createForm.roll_number}
                                onChange={(e) => setCreateForm({ ...createForm, roll_number: e.target.value })} />
                            <TextField select label="Department" fullWidth value={createForm.department_id}
                                onChange={(e) => setCreateForm({ ...createForm, department_id: e.target.value, program_id: '' })}>
                                {departments.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
                            </TextField>
                            <TextField select label="Program" fullWidth value={createForm.program_id}
                                onChange={(e) => setCreateForm({ ...createForm, program_id: e.target.value })}>
                                {filteredPrograms.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
                            </TextField>
                            <Box display="flex" gap={2}>
                                <TextField select label="Year / Semester" fullWidth value={createForm.structure_type}
                                    onChange={(e) => setCreateForm({ ...createForm, structure_type: e.target.value, structure_value: 1 })}>
                                    <MenuItem value="Semester">Semester</MenuItem>
                                    <MenuItem value="Year">Year</MenuItem>
                                </TextField>
                                <TextField select label={createForm.structure_type} fullWidth value={createForm.structure_value}
                                    onChange={(e) => setCreateForm({ ...createForm, structure_value: e.target.value })}>
                                    {[...Array(maxValue)].map((_, i) => (
                                        <MenuItem key={i + 1} value={i + 1}>{i + 1}</MenuItem>
                                    ))}
                                </TextField>
                            </Box>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleCreateUser}>Create</Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar feedback */}
            <Snackbar
                open={snack.open}
                autoHideDuration={4000}
                onClose={() => setSnack({ ...snack, open: false })}
            >
                <Alert severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })}>
                    {snack.msg}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default UserManagement;
